import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import BeamContext from '../../context/BeamContext';
import ConcreteSectionSketch from './ConcreteSectionSketch';
import { openPrintReport } from './openPrintReport';
import {
  eurocodeDesignChecksTitle,
  EurocodeDesignChecksSubtitle,
} from './eurocodeDesignChecksChrome';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler // Import Filler for area under curve if needed
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Helper function to format numbers for tables
const formatTableCell = (num, decimals = 3) => {
  if (num === null || num === undefined || isNaN(num)) return 'N/A';
  return num.toFixed(decimals);
};

const utilBarColor = (u) => {
  if (u == null || isNaN(u)) return 'grey.400';
  if (u <= 0.7) return 'success.main';
  if (u <= 0.9) return 'warning.main';
  return 'error.main';
};

function designCheckTooltipTitle(r) {
  const hasClause = r.clause != null && r.clause !== '';
  const hasHint = r.hint != null && r.hint !== '';
  if (!hasClause && !hasHint) return '';
  return (
    <span style={{ whiteSpace: 'pre-wrap', display: 'block', maxWidth: 360 }}>
      {hasClause && <span>{r.clause}</span>}
      {hasHint && (
        <>
          {hasClause ? <><br /><br /></> : null}
          {typeof r.hint === 'string' ? <span>{r.hint}</span> : r.hint}
        </>
      )}
    </span>
  );
}

function ec2Rows(ec2) {
  if (!ec2 || ec2.error) return [];
  const rows = [];
  const push = (label, clause, util, ok, extra = '', hint = '') => {
    rows.push({ label, clause, utilization: util, ok, extra, hint });
  };
  if (ec2.bending) {
    push(
      'Bending',
      ec2.bending.clause,
      ec2.bending.utilization,
      ec2.bending.ok,
      <>
        |M<sub>Ed</sub>|={ec2.bending.M_Ed_kNm} kNm, M<sub>Rd</sub>={ec2.bending.M_Rd_kNm} kNm
      </>,
    );
  }
  if (ec2.ductility_x_d) {
    const lim = ec2.ductility_x_d.limit || 0.45;
    push(
      'Ductility x/d',
      ec2.ductility_x_d.clause,
      ec2.ductility_x_d.x_over_d / lim,
      ec2.ductility_x_d.ok,
      `x/d=${ec2.ductility_x_d.x_over_d}, limit=${lim}`,
      ec2.ductility_x_d.hint || '',
    );
  }
  if (ec2.reinforcement_limits) {
    const umin = ec2.reinforcement_limits.A_s_m2 / Math.max(ec2.reinforcement_limits.A_s_min_m2, 1e-12);
    push(
      <>A<sub>s</sub> ≥ A<sub>s,min</sub></>,
      ec2.reinforcement_limits.clause,
      umin,
      ec2.reinforcement_limits.ok_min,
      '',
    );
    const umax = ec2.reinforcement_limits.A_s_m2 / Math.max(ec2.reinforcement_limits.A_s_max_m2, 1e-12);
    push(
      <>A<sub>s</sub> ≤ A<sub>s,max</sub></>,
      ec2.reinforcement_limits.clause,
      umax,
      ec2.reinforcement_limits.ok_max,
      '',
    );
  }
  if (ec2.shear_V_Rd_c) {
    push(
      <>Shear V<sub>Rd,c</sub></>,
      ec2.shear_V_Rd_c.clause,
      ec2.shear_V_Rd_c.utilization,
      ec2.shear_V_Rd_c.ok,
      '',
    );
  }
  if (ec2.shear_with_links) {
    push(
      <>Shear strut (V<sub>Rd,max</sub>)</>,
      ec2.shear_with_links.clause,
      ec2.shear_with_links.utilization_strut,
      ec2.shear_with_links.ok,
      ec2.shear_with_links.links_required ? (
        <>
          A<sub>sw</sub>/s req.={ec2.shear_with_links.A_sw_over_s_required_m2_per_m} m²/m
        </>
      ) : '',
    );
  }
  if (ec2.sls_stress_concrete) {
    const u = (ec2.sls_stress_concrete.sigma_c_MPa || 0) / Math.max(ec2.sls_stress_concrete.limit_MPa || 1, 1e-6);
    push('SLS concrete stress', ec2.sls_stress_concrete.clause || '§7.2', u, ec2.sls_stress_concrete.ok, '');
  }
  if (ec2.sls_stress_steel) {
    const u = (ec2.sls_stress_steel.sigma_s_MPa || 0) / Math.max(ec2.sls_stress_steel.limit_MPa || 1, 1e-6);
    const sig = ec2.sls_stress_steel.sigma_s_MPa;
    const lims = ec2.sls_stress_steel.limit_MPa;
    const mqp = ec2.sls_stress_steel.M_qp_kNm;
    push(
      'SLS steel stress',
      ec2.sls_stress_steel.clause || '§7.2',
      u,
      ec2.sls_stress_steel.ok,
      <>
        {sig != null ? `${sig.toFixed(1)} / ${lims?.toFixed(0)} MPa` : ''}
        {mqp != null ? (
          <>
            {' '}
            (M<sub>qp</sub>≈{mqp} kNm)
          </>
        ) : null}
      </>,
      (
        <>
          Quasi-permanent bending: σ<sub>s</sub> ≈ M<sub>qp</sub>/(z·A<sub>s</sub>), compared to 0.8·f<sub>yk</sub> for crack
          control (§7.2).
        </>
      ),
    );
  }
  if (ec2.sls_crack_width) {
    const u = (ec2.sls_crack_width.w_k_mm || 0) / Math.max(ec2.sls_crack_width.w_lim_mm || 0.4, 1e-6);
    push(
      <>Crack width w<sub>k</sub></>,
      ec2.sls_crack_width.clause,
      u,
      ec2.sls_crack_width.ok,
      '',
    );
  }
  if (ec2.sls_deflection) {
    push('SLS deflection', ec2.sls_deflection.clause, ec2.sls_deflection.utilization, ec2.sls_deflection.ok, '');
  }
  return rows;
}

const fmtCheck1 = (x) => (x != null && !Number.isNaN(Number(x)) ? Number(x).toFixed(1) : '—');

function splitEc3CaseLabel(fullLabel) {
  if (!fullLabel) return { detail: '', caseTag: '' };
  const sep = ' — ';
  const i = fullLabel.lastIndexOf(sep);
  if (i < 0) return { detail: fullLabel.trim(), caseTag: '' };
  return { detail: fullLabel.slice(0, i).trim(), caseTag: fullLabel.slice(i + sep.length).trim() };
}

function ec3Rows(ec3, ec3sls) {
  if (!ec3 || ec3.error) return [];
  const rows = [];
  if (ec3.fy_MPa_used != null || ec3.t_governing_mm != null) {
    rows.push({
      label: 'Material fy(t)',
      clause: 'EN 1993-1-1:2022 Table 5.1',
      ok: true,
      extra: `fy=${ec3.fy_MPa_used ?? ec3.fy_MPa} MPa · t_gov=${ec3.t_governing_mm ?? '—'} mm`,
    });
  }
  if (ec3.classification?.standard_note) {
    rows.push({
      label: 'Cold-formed caveat',
      clause: 'EN 1993-1-3',
      ok: true,
      severity: 'warning',
      extra: ec3.classification.standard_note,
    });
  }
  if (ec3.N_Ed_kN != null && Number(ec3.N_Ed_kN) > 0) {
    rows.push({
      label: 'Axial compression input',
      clause: 'EN 1993-1-1:2022 §8.3',
      ok: true,
      extra: `N_Ed=${fmtCheck1(ec3.N_Ed_kN)} kN`,
    });
  }
  const ltbBendingHint = (
    <>
      When an unbraced length or LTB effective length is set, the M<sub>Rd</sub> used in the bending check is the minimum of the
      cross-section resistance and M<sub>b,Rd</sub> from lateral-torsional buckling (EN 1993-1-1 §6.3.2). A separate
      &quot;Lateral-torsional buckling&quot; row is shown when LTB is evaluated.
    </>
  );

  for (const c of ec3.checks || []) {
    // EN 1993-1-1 §5.5 classification: skip a green "OK" row for routine Class 1–3 rolled sections.
    if (c.id === 'classification' && c.ok === true && !c.message) {
      continue;
    }
    const isClassWarning = c.id === 'classification' && c.message;
    const { detail, caseTag } = splitEc3CaseLabel(c.label || '');
    const caseSuffix = caseTag ? ` (${caseTag})` : '';

    let label = c.label || c.id;
    let hint = '';
    let extra;
    if (c.message) {
      extra = c.message;
    } else if (c.class != null) {
      extra = `Class ${c.class}`;
    } else if (c.V_Rd_kN != null) {
      extra = (
        <>
          V<sub>Rd</sub>={fmtCheck1(c.V_Rd_kN)} kN
        </>
      );
    } else if (c.id === 'compression_flexural') {
      label = `Axial compression (flexural buckling)${caseSuffix}`;
      extra = `N_Ed=${fmtCheck1(c.N_Ed_kN)} / N_b,Rd=${fmtCheck1(c.N_b_Rd_kN)} kN`;
    } else if (c.id === 'nm_interaction') {
      label = `N–M interaction Method 2 (§8.3.3)${caseSuffix}`;
      extra = `util=${fmtCheck1(c.utilization)} (8.88=${fmtCheck1(c.utilization_8_88)} · 8.89=${fmtCheck1(c.utilization_8_89)}; kyy=${fmtCheck1(c.k_yy)} · kzy=${fmtCheck1(c.k_zy)}; Cm,y=${c.C_m_y ?? '—'})`;
    } else if (c.M_Rd_kNm != null) {
      extra = (
        <>
          M<sub>Rd</sub>={fmtCheck1(c.M_Rd_kNm)} kNm
        </>
      );
    } else {
      extra = '';
    }

    if (c.id === 'bending') {
      label = `Bending capacity — EN 1993-1-1 §6.2${caseSuffix}`;
      const class4Hint = ec3.class4_effective ? (
        <>
          Class 4 effective: W<sub>eff,el</sub>={ec3.class4_effective.W_eff_mm3} mm³, ρ<sub>f</sub>=
          {ec3.class4_effective.rho_flange}, ρ<sub>w</sub>={ec3.class4_effective.rho_web}
        </>
      ) : null;
      hint = (
        <>
          {detail ? <span style={{ whiteSpace: 'pre-wrap' }}>{detail}</span> : null}
          {class4Hint ? (
            <>
              <br />
              <br />
              {class4Hint}
            </>
          ) : null}
          <br />
          <br />
          {ltbBendingHint}
        </>
      );
      const med = c.M_Ed_kNm;
      const mrd = c.M_Rd_kNm;
      if (c.message && (mrd == null || Number.isNaN(Number(mrd)))) {
        extra = c.message;
      } else {
        extra = (
          <>
            |M<sub>Ed</sub>|={fmtCheck1(med)} / M<sub>Rd</sub>={fmtCheck1(mrd)} kNm
          </>
        );
      }
    } else if (c.id === 'shear') {
      label = `Shear capacity — EN 1993-1-1 §6.2.6 & EN 1993-1-5 §5${caseSuffix}`;
      hint = (
        <>
          {detail ? <span style={{ whiteSpace: 'pre-wrap' }}>{detail}</span> : null}
          {c.V_pl_Rd_kN != null && c.V_bw_Rd_kN != null ? (
            <>
              <br />
              <br />
              V<sub>pl,Rd</sub>={fmtCheck1(c.V_pl_Rd_kN)} kN, V<sub>bw,Rd</sub>={fmtCheck1(c.V_bw_Rd_kN)} kN, V<sub>Rd</sub>=
              {fmtCheck1(c.V_Rd_kN)} kN.
            </>
          ) : null}
          {c.av_note ? (
            <>
              <br />
              <br />
              Shear area: {c.av_note}
            </>
          ) : null}
          {c.shear_buckling_note ? (
            <>
              <br />
              <br />
              Shear buckling (web): {c.shear_buckling_note}
            </>
          ) : null}
        </>
      );
      extra = (
        <>
          |V<sub>Ed</sub>|={fmtCheck1(c.V_Ed_kN)} / V<sub>Rd</sub>={fmtCheck1(c.V_Rd_kN)} kN
        </>
      );
    } else if (c.id === 'ltb') {
      label = `Lateral-torsional buckling — EN 1993-1-1 §6.3.2${caseSuffix}`;
      hint = (
        <>
          {detail ? <span style={{ whiteSpace: 'pre-wrap' }}>{detail}</span> : null}
          {c.message ? (
            <>
              <br />
              <br />
              {c.message}
            </>
          ) : null}
          {c.M_cr_kNm != null ? (
            <>
              <br />
              <br />
              M<sub>cr</sub>≈{fmtCheck1(c.M_cr_kNm)} kNm, λ<sub>LT</sub>≈{fmtCheck1(c.lambda_LT)}, χ<sub>LT</sub>≈
              {fmtCheck1(c.chi_LT)}, M<sub>b,Rd</sub>≈{fmtCheck1(c.M_b_Rd_kNm)} kNm
            </>
          ) : null}
          {c.ltb_fem?.m_cr_model_note ? (
            <>
              <br />
              <br />
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {c.ltb_fem.m_cr_model_note}
              </Typography>
            </>
          ) : null}
        </>
      );
      if (c.M_b_Rd_kNm != null && c.M_Ed_kNm != null) {
        extra = (
          <>
            |M<sub>Ed</sub>|={fmtCheck1(c.M_Ed_kNm)} / M<sub>b,Rd</sub>={fmtCheck1(c.M_b_Rd_kNm)} kNm
          </>
        );
      }
    } else if (c.id === 'mv_interaction') {
      label = `M–V interaction (plastic) — EN 1993-1-1 §6.2.8${caseSuffix}`;
      hint = detail ? <span style={{ whiteSpace: 'pre-wrap' }}>{detail}</span> : '';
      extra = (
        <>
          |M<sub>Ed</sub>|={fmtCheck1(c.M_Ed_kNm)} / M<sub>y,V,Rd</sub>={fmtCheck1(c.M_y_V_Rd_kNm)} kNm
        </>
      );
    } else if (c.id === 'mv_elastic_von_mises') {
      const isCombinedTorsion =
        Boolean(c.elastic_torsion_screening) ||
        (c.sigma_omega_MPa != null && Number(c.sigma_omega_MPa) > 1e-6) ||
        (c.tau_t_MPa != null && Number(c.tau_t_MPa) > 1e-6);
      label = isCombinedTorsion
        ? `Combined bending+shear+torsion (von Mises) — EN 1993-1-1 §6.2.1(5) / §6.2.7${caseSuffix}`
        : `M–V elastic (von Mises) — EN 1993-1-1 §6.2.1(5)${caseSuffix}`;
      hint = (
        <>
          {detail ? <span style={{ whiteSpace: 'pre-wrap' }}>{detail}</span> : null}
          {c.screening_note ? (
            <>
              <br />
              <br />
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {c.screening_note}
              </Typography>
            </>
          ) : null}
          {c.von_Mises_MPa != null ? (
            <>
              <br />
              <br />
              {isCombinedTorsion && c.sigma_x_bending_MPa != null ? (
                <>
                  σ<sub>x,bend</sub>≈{fmtCheck1(c.sigma_x_bending_MPa)} MPa, σ<sub>ω</sub>≈
                  {fmtCheck1(c.sigma_omega_MPa)} MPa → σ<sub>x</sub>≈{fmtCheck1(c.sigma_x_MPa)} MPa; τ<sub>V</sub>≈
                  {fmtCheck1(c.tau_V_MPa)} MPa, τ<sub>t</sub>≈{fmtCheck1(c.tau_t_MPa)} MPa → τ≈{fmtCheck1(c.tau_MPa)}{' '}
                  MPa; √(σ²+3τ²)≈{fmtCheck1(c.von_Mises_MPa)} MPa (f<sub>y</sub>/γ<sub>M0</sub>≈
                  {fmtCheck1(c.limit_MPa)} MPa)
                  {c.W_t_est_mm3 != null ? (
                    <>
                      <br />
                      W<sub>t,est</sub>≈{fmtCheck1(c.W_t_est_mm3)} mm³
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  σ<sub>x</sub>≈{fmtCheck1(c.sigma_x_MPa)} MPa, τ≈{fmtCheck1(c.tau_MPa)} MPa, √(σ²+3τ²)≈
                  {fmtCheck1(c.von_Mises_MPa)} MPa (limit f<sub>y</sub>/γ<sub>M0</sub>≈{fmtCheck1(c.limit_MPa)} MPa)
                </>
              )}
            </>
          ) : null}
        </>
      );
      extra = (
        <>
          σ<sub>vm</sub>={fmtCheck1(c.von_Mises_MPa)} / f<sub>lim</sub>={fmtCheck1(c.limit_MPa)} MPa
          {isCombinedTorsion && c.T_t_Ed_kNm != null && c.B_Ed_kNm2 != null ? (
            <>
              {' '}
              · |T<sub>t,Ed</sub>|={fmtCheck1(c.T_t_Ed_kNm)} kNm, |B<sub>Ed</sub>|={fmtCheck1(c.B_Ed_kNm2)} kNm²
            </>
          ) : null}
        </>
      );
    } else if (c.id === 'ltb_scope_elastic_twist') {
      label = `LTB / elastic M<sub>cr</sub> scope — torsional rotation screening${caseSuffix}`;
      hint = (
        <>
          {c.clause ? <span style={{ whiteSpace: 'pre-wrap' }}>{c.clause}</span> : null}
          <br />
          <br />
          Combined bending+torsion at the cross-section is in the von Mises / §6.2.7 row. The lateral-torsional buckling /
          M<sub>cr</sub> check does not use this first-order twist. Imperfection-based LTB models assume small rotations; when
          linear elastic twist from eccentricity exceeds the screening limit, general-method assumptions may not apply —
          consider GMNIA or a specialist stability check if twist governs.
        </>
      );
      extra = c.message ? <span style={{ whiteSpace: 'pre-wrap' }}>{c.message}</span> : '';
    } else {
      hint = [c.clause && detail !== c.label ? detail : '', c.message].filter(Boolean).join('\n\n');
    }

    rows.push({
      label,
      clause: c.clause || '',
      hint,
      utilization: c.utilization != null ? c.utilization : (c.ok === false ? 1 : 0),
      ok: c.ok !== false,
      severity:
        c.severity === 'warning' || isClassWarning ? 'warning' : c.ok === false ? 'error' : 'success',
      extra,
    });
  }
  if (ec3sls && !ec3sls.error) {
    const w = ec3sls.deflection_mm;
    const lim = ec3sls.limit_mm;
    rows.push({
      label: ec3sls.label || 'Deflection',
      clause: ec3sls.clause || '',
      hint: 'SLS deflection compared to the limiting value from span/(deflection limit ratio) (EN 1993-1-1 §7.2).',
      utilization: ec3sls.utilization,
      ok: ec3sls.ok,
      severity: ec3sls.ok ? 'success' : 'error',
      extra: (
        <>
          w<sub>Ed</sub>={fmtCheck1(w)} / w<sub>Rd</sub>={fmtCheck1(lim)} mm
        </>
      ),
    });
  }
  return rows;
}

function ec5Rows(ec5, ec5sls) {
  if (!ec5 || ec5.error) return [];
  const rows = (ec5.checks || []).map((c) => ({
    label: c.label || c.id,
    clause: c.clause || '',
    utilization: c.utilization != null ? c.utilization : 0,
    ok: c.ok !== false,
    extra:
      c.id === 'ltb' && c.k_crit != null ? (
        <>
          l<sub>ef</sub>={c.l_ef_m} m, k<sub>crit</sub>={c.k_crit}, λ<sub>rel,m</sub>={c.lambda_rel_m}
        </>
      ) : c.M_Ed_kNm != null ? (
        <>
          M<sub>Ed</sub>={c.M_Ed_kNm} kNm
        </>
      ) : c.tau_d_MPa != null ? (
        <>
          τ<sub>d</sub>={c.tau_d_MPa} MPa
        </>
      ) : (
        ''
      ),
  }));
  if (ec5sls && !ec5sls.error) {
    rows.push({
      label: ec5sls.label || 'Deflection',
      clause: ec5sls.clause || '',
      utilization: Math.max(ec5sls.utilization_net || 0, ec5sls.utilization_appearance || 0),
      ok: ec5sls.ok,
      extra: (
        <>
          w<sub>fin</sub>={ec5sls.w_fin_mm} mm
        </>
      ),
    });
  }
  return rows;
}

function DesignChecksPanel({ designChecks }) {
  if (!designChecks || Object.keys(designChecks).length === 0) return null;
  const panels = [
    { key: 'ec2', title: 'EN 1992-1-1 (Concrete)', rows: ec2Rows(designChecks.ec2), err: designChecks.ec2?.error },
    { key: 'ec3', title: 'EN 1993-1-1 (Steel)', rows: ec3Rows(designChecks.ec3, designChecks.ec3_sls), err: designChecks.ec3?.error },
    { key: 'ec5', title: 'EN 1995-1-1 (Timber)', rows: ec5Rows(designChecks.ec5, designChecks.ec5_sls), err: designChecks.ec5?.error },
  ];
  const naFi = designChecks.eurocode_na || designChecks.national_annex_finland;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>{eurocodeDesignChecksTitle(designChecks)}</Typography>
      <EurocodeDesignChecksSubtitle designChecks={designChecks} naFi={naFi} />
      {panels.map((p) => {
        if (!designChecks[p.key] && !p.err) return null;
        return (
          <Accordion key={p.key} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">{p.title}</Typography>
              {designChecks[p.key]?.overall_ok === false && (
                <Chip size="small" color="error" label="FAIL" sx={{ ml: 2 }} />
              )}
              {designChecks[p.key]?.overall_ok === true && (
                <Chip size="small" color="success" label="OK" sx={{ ml: 2 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              {p.err && (
                <Alert severity="error" sx={{ mb: 1 }}>{String(p.err)}</Alert>
              )}
              {!p.err && p.key === 'ec3' && designChecks.ec3?.standard && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {designChecks.ec3.standard}
                </Typography>
              )}
              {!p.err && p.key === 'ec3' && designChecks.ec3?.compliance_matrix?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Scope: {designChecks.ec3.compliance_matrix.map((m) => `${m.check} (${m.status})`).join(' · ')}
                </Typography>
              )}
              {!p.err && p.rows.length === 0 && (
                <Typography variant="body2" color="text.secondary">No checks for this standard.</Typography>
              )}
              {!p.err && p.rows.map((r, i) => {
                const tooltipTitle = designCheckTooltipTitle(r);
                const labelEl = (
                  <Typography variant="body2" sx={{ minWidth: 160 }} component="span">
                    {r.label}
                  </Typography>
                );
                return (
                <Box key={i} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {tooltipTitle ? <Tooltip title={tooltipTitle}>{labelEl}</Tooltip> : labelEl}
                    {r.severity === 'warning' ? (
                      <Chip size="small" label="Warning" color="warning" />
                    ) : (
                      <Chip size="small" label={r.ok ? 'OK' : 'FAIL'} color={r.ok ? 'success' : 'error'} />
                    )}
                    <Typography variant="caption" color="text.secondary" component="span">
                      {r.extra}
                    </Typography>
                  </Box>
                  {r.utilization != null && !isNaN(r.utilization) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, r.utilization * 100))}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          '& .MuiLinearProgress-bar': { bgcolor: utilBarColor(Math.min(r.utilization, 1)) },
                        }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 56, textAlign: 'right' }}>
                        {(() => {
                          const pct = r.utilization * 100;
                          return pct > 10000 ? `${pct.toExponential(2)}%` : `${pct.toFixed(1)}%`;
                        })()}
                      </Typography>
                    </Box>
                  )}
                </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}


const ResultsDisplay = ({ stackWithVisualization = false, beamSvgRef }) => {
  const {
    results,
    loading,
    error,
    resultsOutOfDate,
    spans,
    advancedOptions,
    materialFamily,
    selectedSection,
    selectedType,
  } = useContext(BeamContext);
  const [resultsDetailOpen, setResultsDetailOpen] = useState(true);
  const momentChartRef = useRef(null);
  const shearChartRef = useRef(null);
  const deflectionChartRef = useRef(null);
  const torsionStVChartRef = useRef(null);
  const torsionWarpChartRef = useRef(null);

  const handlePrintReport = useCallback(() => {
    if (!results) return;
    const chartToDataUrl = (ref) => {
      const inst = ref.current;
      if (inst && typeof inst.toBase64Image === 'function') {
        return inst.toBase64Image('image/png', 1);
      }
      return '';
    };
    const beamSvgHtml = beamSvgRef?.current?.outerHTML || '';
    openPrintReport({
      results,
      spans,
      advancedOptions,
      materialFamily,
      selectedSection,
      selectedType,
      beamSvgHtml,
      chartImages: {
        moment: chartToDataUrl(momentChartRef),
        shear: chartToDataUrl(shearChartRef),
        deflection: chartToDataUrl(deflectionChartRef),
        torsionStV: chartToDataUrl(torsionStVChartRef),
        torsionWarp: chartToDataUrl(torsionWarpChartRef),
      },
    });
  }, [
    results,
    spans,
    advancedOptions,
    materialFamily,
    selectedSection,
    selectedType,
    beamSvgRef,
    torsionStVChartRef,
    torsionWarpChartRef,
  ]);

  useEffect(() => {
    if (resultsOutOfDate) setResultsDetailOpen(false);
  }, [resultsOutOfDate]);

  useEffect(() => {
    if (results && !resultsOutOfDate) setResultsDetailOpen(true);
  }, [results, resultsOutOfDate]);

  useEffect(() => {
    if (loading.calculation) setResultsDetailOpen(true);
  }, [loading.calculation]);

  // Linear x = physical position (m). Avoid category scale + toFixed(2) labels (collapsed points)
  // and y toFixed(3) (self-weight M/V became stair-plateaus).
  const getChartOptions = (titleText, yAxisLabel) => ({
    responsive: true,
    maintainAspectRatio: false,
    parsing: { xAxisKey: 'x', yAxisKey: 'y' },
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: titleText },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        axis: 'x',
        callbacks: {
          title(items) {
            if (items.length && items[0].parsed.x != null) {
              return `Position ${items[0].parsed.x.toFixed(4)} m`;
            }
            return '';
          },
          label(context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            const y = context.parsed.y;
            if (y != null && !Number.isNaN(y)) {
              const a = Math.abs(y);
              label += a >= 0.01 ? y.toFixed(4) : a >= 1e-6 ? y.toExponential(3) : y.toExponential(2);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: 'Position (m)' },
        ticks: {
          callback: (v) => (typeof v === 'number' ? v.toFixed(2) : v),
        },
      },
      y: {
        title: { display: true, text: yAxisLabel },
      },
    },
    elements: {
      point: { radius: 0 },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  });

  const prepareChartData = (xValues, yValues, label, borderColor, options = {}) => {
    const { unit, conversionFactor = 1 } = options;
    const datasetLabel = unit ? `${label} (${unit})` : label;
    const xs = xValues || [];
    const ys = yValues || [];
    const data = xs.map((x, i) => {
      const raw = ys[i];
      const y =
        raw === null || raw === undefined || Number.isNaN(raw)
          ? NaN
          : Number(raw) * conversionFactor;
      return { x: Number(x), y };
    });
    return {
      datasets: [
        {
          label: datasetLabel,
          data,
          borderColor,
          backgroundColor: `${borderColor}80`,
          tension: 0,
        },
      ],
    };
  };

  // Deflection: Green, m -> mm
  const deflectionChartData = results?.x_coords && results?.deflection ? 
    prepareChartData(results.x_coords, results.deflection, 'Deflection', 'rgb(75, 192, 192)', { unit: 'mm', conversionFactor: 1 }) 
    : null;

  // Shear Force: Red, N -> kN
  const shearChartData = results?.x_coords && results?.shear_force ? 
    prepareChartData(results.x_coords, results.shear_force, 'Shear Force', 'rgb(255, 99, 132)', { unit: 'kN', conversionFactor: 1 }) 
    : null;

  // Bending Moment: Blue, Nm -> kNm
  const momentChartData = results?.x_coords && results?.bending_moment ? 
    prepareChartData(results.x_coords, results.bending_moment, 'Bending Moment', 'rgb(53, 162, 235)', { unit: 'kNm', conversionFactor:1 }) 
    : null;

  // Show torsion diagrams whenever the 14-DOF steel line model ran (even if torque ≈ 0), so
  // eccentricity effects are visible when present and the charts are not hidden for the e_y=0 case.
  const showTorsionCharts =
    results?.x_coords &&
    results?.analysis_meta?.beam_model === '14dof_prismatic_centroid' &&
    Array.isArray(results?.torsion_st_venant_knm) &&
    results.torsion_st_venant_knm.length > 0;

  const torsionStVChartData =
    showTorsionCharts && results?.torsion_st_venant_knm
      ? prepareChartData(
          results.x_coords,
          results.torsion_st_venant_knm,
          'St. Venant torsion',
          'rgb(153, 102, 255)',
          { unit: 'kNm', conversionFactor: 1 },
        )
      : null;

  const torsionWarpChartData =
    showTorsionCharts && results?.torsion_warping_knm
      ? prepareChartData(
          results.x_coords,
          results.torsion_warping_knm,
          'Warping torsion',
          'rgb(255, 159, 64)',
          { unit: 'kNm', conversionFactor: 1 },
        )
      : null;

  const showTwistCol =
    results?.nodal_displacements?.some((n) => n.twist_rad != null) ?? false;

  const metaEccY = Number(results?.analysis_meta?.input_max_abs_eccentricity_y_m);
  const metaEccZ = Number(results?.analysis_meta?.input_max_abs_eccentricity_z_m);
  const showPlanarVsEccentricityNote =
    results?.analysis_meta?.beam_model === '14dof_prismatic_centroid' &&
    ((Number.isFinite(metaEccY) && metaEccY > 1e-9) ||
      (Number.isFinite(metaEccZ) && metaEccZ > 1e-9));

  return (
    <Paper elevation={3} sx={{ p: 2, mt: stackWithVisualization ? 0 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h6" sx={{ mb: 0 }}>Calculation Results</Typography>
        {results && !loading.calculation && !error.calculation && (
          <Tooltip
            title={
              resultsOutOfDate
                ? 'Report uses last calculated results; inputs changed — recalculate to match. Expand “Diagrams…” below so chart images are included.'
                : 'Open print preview (Save as PDF). Expand “Diagrams…” first so charts appear in the report.'
            }
          >
            <IconButton
              size="small"
              color="primary"
              aria-label="Print calculation report"
              onClick={handlePrintReport}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {loading.calculation && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
      {error.calculation && !loading.calculation && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Calculation Error: {error.calculation}
        </Alert>
      )}
      {results && !loading.calculation && !error.calculation && (
        <Accordion
          expanded={resultsDetailOpen}
          onChange={(_, expanded) => setResultsDetailOpen(expanded)}
          disableGutters
          elevation={0}
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight="medium">
                Diagrams, design checks, and tables
              </Typography>
              {resultsOutOfDate && (
                <Chip size="small" label="Inputs changed — recalculate to refresh" color="warning" variant="outlined" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 0 }}>
        <Box sx={{ mt: 0 }}>
          {results.analysis_meta && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Loads: {results.analysis_meta.loads_basis === 'characteristic' ? 'characteristic (ULS factored M/V; deflection quasi-permanent)' : 'direct (as entered)'}
              {results.analysis_meta.input_max_abs_eccentricity_y_m != null && (
                <>
                  {' '}
                  · max |e<sub>y</sub>| in request = {results.analysis_meta.input_max_abs_eccentricity_y_m} m (centreline
                  offset; UI mm → m)
                </>
              )}
              {results.analysis_meta.input_max_abs_eccentricity_z_m != null && (
                <>
                  {' '}
                  · max |e<sub>z</sub>| in request = {results.analysis_meta.input_max_abs_eccentricity_z_m} m
                </>
              )}
              {results.analysis_meta.ltb_c1 != null && (
                <> · LTB c₁ (auto) ≈ {results.analysis_meta.ltb_c1}</>
              )}
              {results.analysis_meta.M_Ed_qp_kNm != null && (
                <>
                  {' '}
                  · |M| quasi-permanent (M<sub>Ed,qp</sub>) ≈ {results.analysis_meta.M_Ed_qp_kNm} kNm
                </>
              )}
              {results.analysis_meta.moment_envelope && (
                <>
                  {' '}
                  · M<sub>Ed,sag</sub>≈{results.analysis_meta.moment_envelope.M_Ed_sag_kNm} kNm (V<sub>Ed,sag</sub>≈
                  {results.analysis_meta.moment_envelope.V_Ed_sag_kN} kN), M<sub>Ed,hog</sub>≈
                  {results.analysis_meta.moment_envelope.M_Ed_hog_kNm} kNm (V<sub>Ed,hog</sub>≈
                  {results.analysis_meta.moment_envelope.V_Ed_hog_kN} kN)
                </>
              )}
              {results.analysis_meta.beam_model === '14dof_prismatic_centroid' && (
                <>
                  {' '}
                  · 14-DOF line model (eccentricity / shear-centre coupling). Major-axis M, V, deflection diagrams are
                  from a planar centroid load case; e<sub>y</sub> drives St.&nbsp;Venant / warping torque and lateral
                  twist (see charts and nodal table).
                  {results.analysis_meta.beam_14dof_line_node_count != null && (
                    <>
                      {' '}
                      Line mesh: {results.analysis_meta.beam_14dof_line_node_count} nodes
                      {results.analysis_meta.beam_14dof_torque_mesh_refined
                        ? ' (extra node(s) for eccentric UDL torque)'
                        : ''}
                      . |T<sub>St.V.</sub>|_max ≈{' '}
                      {results.analysis_meta.beam_14dof_max_abs_st_venant_knm ?? '—'} kNm; max |φ| ≈{' '}
                      {results.analysis_meta.beam_14dof_max_abs_twist_rad ?? '—'} rad; max |u<sub>y</sub>| ≈{' '}
                      {results.analysis_meta.beam_14dof_max_abs_lateral_mm ?? '—'} mm.
                    </>
                  )}
                </>
              )}
            </Typography>
          )}
          {results.analysis_meta?.ltb_elastic_twist_scope_ok === false && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                First-order elastic twist from the 14-DOF model exceeds the small-rotation screening limit used for
                linearized LTB / elastic M<sub>cr</sub> (EN 1993-1-1 §6.3.2-style) scope. Combined bending+torsion is still
                reflected in the von Mises / §6.2.7 check; the LTB row does not use this twist. M<sub>cr</sub> utilization is
                not a substitute for stability at large rotation without further analysis.
              </Typography>
              {results.analysis_meta.ltb_twist_scope_note ? (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  {results.analysis_meta.ltb_twist_scope_note}
                </Typography>
              ) : null}
            </Alert>
          )}
          {results.design_checks && Object.keys(results.design_checks).length > 0 && (
            <DesignChecksPanel designChecks={results.design_checks} />
          )}
          {results.concrete_section && (
            <ConcreteSectionSketch section={results.concrete_section} />
          )}
          {/* Max/Min Summary - Commented out as backend does not provide these directly yet */}
          {/* 
           <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Typography variant="body2">Max Shear:</Typography>
                    <Typography variant="body1"><b>{formatNumber(results.max_shear)} N</b></Typography>
                </Grid>
                 <Grid item xs={6} sm={3}>
                    <Typography variant="body2">Max Moment:</Typography>
                    <Typography variant="body1"><b>{formatNumber(results.max_moment)} Nm</b></Typography>
                </Grid>
                 <Grid item xs={6} sm={3}>
                    <Typography variant="body2">Max Deflection:</Typography>
                    <Typography variant="body1"><b>{formatNumber(results.max_deflection_val)} m</b></Typography>
                </Grid>
                 <Grid item xs={6} sm={3}>
                    <Typography variant="body2">Min Deflection:</Typography>
                    <Typography variant="body1"><b>{formatNumber(results.min_deflection)} m</b></Typography>
                </Grid>
            </Grid>
          */}

          {/* Diagrams - Order: Bending Moment (Blue), Shear Force (Red), Deflection (Green) */}
          {showPlanarVsEccentricityNote && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Bending moment, shear, and deflection diagrams below use a <strong>planar centroid</strong> load
              case (eccentricity is not applied there). With non-zero <em>e</em>
              <sub>y</sub>/<em>e</em>
              <sub>z</sub>, look at <strong>St.&nbsp;Venant / warping torsion</strong> charts, twist and lateral
              displacement in the nodal table, and the 14-DOF note in the caption above — not at the blue M
              diagram for horizontal offset effects.
            </Alert>
          )}
          {momentChartData && (
            <Box sx={{ height: 300, mb: 3 }}>
              <Line
                ref={momentChartRef}
                options={getChartOptions('Bending Moment Diagram', 'Moment (kNm)')}
                data={momentChartData}
              />
            </Box>
          )}
          {shearChartData && (
            <Box sx={{ height: 300, mb: 3 }}>
              <Line
                ref={shearChartRef}
                options={getChartOptions('Shear Force Diagram', 'Shear (kN)')}
                data={shearChartData}
              />
            </Box>
          )}
          {deflectionChartData && (
            <Box sx={{ height: 300, mb: 3 }}>
              <Line
                ref={deflectionChartRef}
                options={getChartOptions('Deflection Diagram', 'Deflection (mm)')}
                data={deflectionChartData}
              />
            </Box>
          )}
          {torsionStVChartData && (
            <Box sx={{ height: 300, mb: 3 }}>
              <Line
                ref={torsionStVChartRef}
                options={getChartOptions('St. Venant torsion', 'Torque (kNm)')}
                data={torsionStVChartData}
              />
            </Box>
          )}
          {torsionWarpChartData && (
            <Box sx={{ height: 300, mb: 3 }}>
              <Line
                ref={torsionWarpChartRef}
                options={getChartOptions('Warping torsion', 'Torque (kNm)')}
                data={torsionWarpChartData}
              />
            </Box>
          )}

          {/* Nodal Displacements Table */}
          {results.nodal_displacements && results.nodal_displacements.length > 0 && (
             <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Nodal Displacements</Typography>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Node ID</TableCell>
                                <TableCell align="right">Position (m)</TableCell>
                                <TableCell align="right">Vertical Disp. (mm)</TableCell>
                                <TableCell align="right">Rotation (mrad)</TableCell>
                                {showTwistCol && (
                                  <TableCell align="right">Twist φ (rad)</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.nodal_displacements.map((node_res, index) => (
                                <TableRow key={index}>
                                    <TableCell>{node_res.node_id}</TableCell>
                                    <TableCell align="right">{formatTableCell(node_res.x)}</TableCell>
                                    <TableCell align="right">{formatTableCell(node_res.vertical_displacement)}</TableCell>
                                    <TableCell align="right">{formatTableCell(node_res.rotation, 5)}</TableCell>
                                    {showTwistCol && (
                                      <TableCell align="right">
                                        {node_res.twist_rad != null
                                          ? formatTableCell(node_res.twist_rad, 6)
                                          : '—'}
                                      </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
             </Box>
          )}

          {/* Reactions Table */}
          {results.reactions && results.reactions.length > 0 && (
             <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Support Reactions</Typography>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Node ID</TableCell>
                                <TableCell align="right">Position (m)</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Value</TableCell>
                                <TableCell>Unit</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.reactions.map((reaction, index) => (
                                <TableRow key={index}>
                                    <TableCell>{reaction.node_id}</TableCell>
                                    <TableCell align="right">
                                      {reaction.x_m != null ? formatTableCell(reaction.x_m) : '—'}
                                    </TableCell>
                                    <TableCell>{reaction.type}</TableCell>
                                    <TableCell align="right">{formatTableCell(reaction.value, 3)}</TableCell>
                                    <TableCell>
                                      {reaction.unit ??
                                        (String(reaction.type || '').startsWith('Force') ? 'kN' : 'kNm')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
             </Box>
          )}

        </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {!results && !loading.calculation && !error.calculation && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Enter parameters and calculate to see results.</Typography>
      )}
    </Paper>
  );
};

export default ResultsDisplay; 